import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Badge, gbp, Loading } from "@/components/ui-bits";
import { toast } from "sonner";
import { ClipboardList, Plus, X, Loader2, Trash2, MapPin, Hammer, Calendar, Paperclip, MessageSquare, Send, FileText, LayoutGrid, Rows3, Pencil, AlertTriangle, CheckCircle2, ArrowLeft } from "lucide-react";

const STATUSES = ["Open", "In Progress", "On Hold", "Completed", "Cancelled"];
const STATUS_STYLE = {
  Open: "bg-blue-50 text-blue-700 ring-blue-200",
  "In Progress": "bg-amber-50 text-amber-700 ring-amber-200",
  "On Hold": "bg-slate-100 text-slate-600 ring-slate-200",
  Completed: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  Cancelled: "bg-red-50 text-red-600 ring-red-200",
};
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const URGENCY_STYLE = {
  Low: "bg-slate-100 text-slate-600 ring-slate-200",
  Medium: "bg-amber-100 text-amber-700 ring-amber-200",
  High: "bg-red-100 text-red-700 ring-red-200",
  Urgent: "bg-red-200 text-red-800 ring-red-300",
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");
const empty = { title: "", property_ref: "", property_id: "", contractor_id: "", priority: "Medium", pricing_mode: "quote", quote_total: "", cost: "", target_date: "", description: "", tenant_name: "", tenant_phone: "", access_details: "", date_reported: "", travel_hours: "", labour_hours: "", notes: "" };

export default function WorkOrders() {
  const [orders, setOrders] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("All");
  const [view, setView] = useState("table");
  const [editing, setEditing] = useState(null);
  const [files, setFiles] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  const load = () => api.get("/work-orders").then(({ data }) => setOrders(data));
  useEffect(() => {
    load();
    api.get("/contractors").then(({ data }) => setContractors(data)).catch(() => {});
    api.get("/properties").then(({ data }) => setProperties(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const p = location.state?.prefill;
    if (p) {
      setEditing(null);
      setForm({ ...empty, title: p.title || "", property_ref: p.property_ref || "", description: p.description || "", priority: p.priority || "Medium", request_id: p.request_id || "", property_id: p.property_id || "" });
      setOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const closeModal = () => { setOpen(false); setEditing(null); setForm(empty); setFiles([]); };
  const openNew = () => { setEditing(null); setForm(empty); setFiles([]); setOpen(true); };
  const openEdit = (o) => {
    setEditing(o);
    setForm({
      title: o.title || "", property_ref: o.property_ref || "", property_id: o.property_id || "", contractor_id: o.contractor_id || "",
      priority: o.priority || "Medium", pricing_mode: o.pricing_mode || (Number(o.quote_total) > 0 ? "quote" : "hourly"),
      quote_total: o.quote_total || "", cost: o.cost || "", target_date: toDateInput(o.target_date),
      description: o.description || "", tenant_name: o.tenant_name || "", tenant_phone: o.tenant_phone || "", access_details: o.access_details || "",
      date_reported: toDateInput(o.date_reported), travel_hours: o.travel_hours || "", labour_hours: o.labour_hours || "", notes: o.notes || "",
    });
    setFiles([]); setOpen(true);
  };

  const onPropertyChange = (ref) => {
    const p = properties.find((x) => x.reference === ref);
    setForm((f) => ({ ...f, property_ref: ref, property_id: p?.property_id || "", tenant_name: p?.tenant || "", tenant_phone: p?.tenant_phone || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const isQuote = form.pricing_mode === "quote";
    const payload = {
      ...form,
      pricing_mode: form.pricing_mode || "hourly",
      quote_total: isQuote ? (parseFloat(form.quote_total) || 0) : 0,
      cost: isQuote ? 0 : (parseFloat(form.cost) || 0),
      travel_hours: isQuote ? 0 : (parseFloat(form.travel_hours) || 0),
      labour_hours: isQuote ? 0 : (parseFloat(form.labour_hours) || 0),
      contractor_id: form.contractor_id || null, target_date: form.target_date || null, date_reported: form.date_reported || null,
    };
    try {
      if (editing) {
        await api.patch(`/work-orders/${editing.work_order_id}`, payload);
        toast.success("Work order updated");
      } else {
        const { data: wo } = await api.post("/work-orders", payload);
        for (const f of files) {
          const fd = new FormData();
          fd.append("entity_type", "work_order");
          fd.append("entity_id", wo.work_order_id);
          fd.append("bucket", "attachments");
          fd.append("file", f);
          await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        toast.success(files.length ? `Work order created with ${files.length} file${files.length > 1 ? "s" : ""} attached` : "Work order created");
      }
      closeModal(); load();
    } catch { toast.error(editing ? "Could not update work order" : "Could not create work order"); }
    finally { setBusy(false); }
  };

  const setLocal = (id, patch) => setOrders((prev) => prev.map((o) => o.work_order_id === id ? { ...o, ...patch } : o));
  const commit = async (o, patch) => { try { await api.patch(`/work-orders/${o.work_order_id}`, patch); } catch { toast.error("Could not save"); load(); } };
  const totalCost = (o) => Number(o.quote_total || 0) > 0 ? Number(o.quote_total) : Number(o.cost || 0) + (Number(o.travel_hours || 0) + Number(o.labour_hours || 0)) * Number(o.labour_rate || 25);

  const setStatus = async (wo, status) => {
    setOrders((prev) => prev.map((o) => o.work_order_id === wo.work_order_id ? { ...o, status } : o));
    await api.patch(`/work-orders/${wo.work_order_id}`, { status });
  };

  const decideQuote = async (wo, action) => {
    try {
      const { data } = await api.post(`/work-orders/${wo.work_order_id}/quote/${action}`);
      setOrders((prev) => prev.map((o) => o.work_order_id === wo.work_order_id ? { ...o, ...data } : o));
      toast.success(`Quote ${action === "accept" ? "accepted" : "declined"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update quote"); }
  };

  const remove = async (wo) => {
    await api.delete(`/work-orders/${wo.work_order_id}`);
    setOrders((prev) => prev.filter((o) => o.work_order_id !== wo.work_order_id));
    toast.success("Work order deleted");
  };

  const shown = orders ? (filter === "All" ? orders : orders.filter((o) => o.status === filter)) : [];
  const counts = orders ? STATUSES.reduce((a, s) => ({ ...a, [s]: orders.filter((o) => o.status === s).length }), {}) : {};
  const openCount = orders ? orders.filter((o) => !["Completed", "Cancelled"].includes(o.status)).length : 0;
  const urgentCount = orders ? orders.filter((o) => ["High", "Urgent"].includes(o.priority) && !["Completed", "Cancelled"].includes(o.status)).length : 0;
  const completedCount = orders ? orders.filter((o) => o.status === "Completed").length : 0;

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 fade-up">
        <div>
          <button data-testid="wo-back-to-maintenance" onClick={() => navigate("/maintenance")}
            className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.2em] text-blue-700 hover:text-blue-900 mb-2 transition-colors">
            <ArrowLeft size={14} /> Maintenance
          </button>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Work Orders</h1>
          <p className="text-slate-500 mt-1 text-sm">Raise jobs, assign contractors and track them to completion</p>
        </div>
        <button data-testid="new-work-order-button" onClick={openNew}
          className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 text-sm font-medium flex items-center gap-2 transition-colors">
          <Plus size={16} /> New Work Order
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <SummaryCard label="Open Jobs" value={openCount} icon={ClipboardList} tone="blue" testid="wo-stat-open" />
        <SummaryCard label="Urgent Jobs" value={urgentCount} icon={AlertTriangle} tone="red" testid="wo-stat-urgent" />
        <SummaryCard label="Completed Jobs" value={completedCount} icon={CheckCircle2} tone="emerald" testid="wo-stat-completed" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap gap-2">
          {["All", ...STATUSES].map((s) => (
            <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)}
              className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {s} {s !== "All" && <span className="opacity-70">({counts[s] || 0})</span>}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          <button data-testid="wo-view-table" onClick={() => setView("table")} className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md transition-colors ${view === "table" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}><Rows3 size={15} /> Table</button>
          <button data-testid="wo-view-cards" onClick={() => setView("cards")} className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md transition-colors ${view === "cards" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}><LayoutGrid size={15} /> Cards</button>
        </div>
      </div>

      {!orders ? <Loading /> : shown.length === 0 ? (
        <Card className="p-10 text-center text-slate-400" data-testid="work-orders-empty">No work orders {filter !== "All" ? `with status "${filter}"` : "yet"}. Click “New Work Order” to raise one.</Card>
      ) : view === "table" ? (
        <TableTracker shown={shown} properties={properties} setLocal={setLocal} commit={commit} setStatus={setStatus} totalCost={totalCost} openEdit={openEdit} remove={remove} />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {shown.map((o) => (
            <Card key={o.work_order_id} className="p-5 flex flex-col" data-testid={`work-order-${o.work_order_id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <p className="text-xs font-mono text-slate-400">{o.reference}</p>
                  <p className="font-semibold text-slate-900 leading-snug">{o.title}</p>
                </div>
                <Badge>{o.priority}</Badge>
              </div>
              {o.description && <p className="text-sm text-slate-500 mb-3 line-clamp-2">{o.description}</p>}
              {o.quote_status && (
                <div className="mb-3 rounded-lg bg-slate-50 border border-slate-100 p-2.5" data-testid={`wo-quote-${o.work_order_id}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold tabular-nums text-slate-800">{gbp(o.quote_amount)}</span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${o.quote_status === "Accepted" ? "bg-emerald-100 text-emerald-700" : o.quote_status === "Declined" ? "bg-slate-200 text-slate-500" : "bg-blue-100 text-blue-700"}`}>Quote {o.quote_status}</span>
                  </div>
                  {o.quote_notes && <p className="text-xs text-slate-400 mt-0.5">{o.quote_notes}</p>}
                  {o.quote_status === "Submitted" && (
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => decideQuote(o, "accept")} data-testid={`wo-accept-${o.work_order_id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3 py-1 text-xs font-semibold">Accept</button>
                      <button onClick={() => decideQuote(o, "decline")} data-testid={`wo-decline-${o.work_order_id}`} className="border border-slate-300 text-slate-600 hover:bg-slate-100 rounded-lg px-3 py-1 text-xs font-semibold">Decline</button>
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-1.5 text-xs text-slate-500 mb-4">
                {(o.property_address || o.property_ref) && <p className="flex items-center gap-1.5"><MapPin size={12} /> {o.property_address || o.property_ref}</p>}
                {o.contractor_name && <p className="flex items-center gap-1.5"><Hammer size={12} /> {o.contractor_name}{o.trade ? ` · ${o.trade}` : ""}</p>}
                {o.target_date && <p className="flex items-center gap-1.5"><Calendar size={12} /> Target {fmtDate(o.target_date)}</p>}
                {totalCost(o) > 0 && <p className="font-medium text-slate-700">{gbp(totalCost(o))}</p>}
              </div>
              <div className="mt-auto flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
                <select data-testid={`wo-status-${o.work_order_id}`} value={o.status} onChange={(e) => setStatus(o, e.target.value)}
                  className={`text-xs font-medium rounded-full px-2.5 py-1 ring-1 ring-inset focus:outline-none ${STATUS_STYLE[o.status]}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button data-testid={`wo-delete-${o.work_order_id}`} onClick={() => remove(o)} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button>
              </div>
              <WorkOrderComms workOrderId={o.work_order_id} contractorName={o.contractor_name} />
            </Card>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">{editing ? "Edit Work Order" : "New Work Order"}</h3>
              <button type="button" data-testid="close-work-order" onClick={closeModal} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Job description</label>
                <input data-testid="wo-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Replace broken boiler" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Property / Address</label>
                <select data-testid="wo-property" value={form.property_ref} onChange={(e) => onPropertyChange(e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                  <option value="">— No property —</option>
                  {properties.map((p) => <option key={p.property_id} value={p.reference}>{p.address}</option>)}
                </select></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Tenant name</label>
                  <input data-testid="wo-tenant-name" value={form.tenant_name} onChange={(e) => setForm({ ...form, tenant_name: e.target.value })} placeholder="e.g. Clara Bright" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Tenant phone</label>
                  <input data-testid="wo-tenant-phone" value={form.tenant_phone} onChange={(e) => setForm({ ...form, tenant_phone: e.target.value })} placeholder="e.g. 07789 000000" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Access details <span className="text-xs font-normal text-slate-400">(key safe code, when to access, parking, etc.)</span></label>
                <textarea data-testid="wo-access-details" rows={2} value={form.access_details} onChange={(e) => setForm({ ...form, access_details: e.target.value })} placeholder="e.g. Key safe 0161 by front door · tenant home after 5pm" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Contractor</label>
                <select data-testid="wo-contractor" value={form.contractor_id} onChange={(e) => setForm({ ...form, contractor_id: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                  <option value="">— Unassigned —</option>
                  {contractors.map((c) => <option key={c.contractor_id} value={c.contractor_id}>{c.name}{c.trade ? ` (${c.trade})` : ""}</option>)}
                </select></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Urgency</label>
                  <select data-testid="wo-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                    {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                  </select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Scheduled date</label>
                  <input data-testid="wo-target-date" type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Date reported</label>
                <input data-testid="wo-date-reported" type="date" value={form.date_reported} onChange={(e) => setForm({ ...form, date_reported: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">How is this priced?</label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" data-testid="wo-mode-quote" onClick={() => setForm({ ...form, pricing_mode: "quote" })}
                    className={`rounded-lg px-3 py-2 text-sm font-medium border transition-colors ${form.pricing_mode === "quote" ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>Quote</button>
                  <button type="button" data-testid="wo-mode-hourly" onClick={() => setForm({ ...form, pricing_mode: "hourly" })}
                    className={`rounded-lg px-3 py-2 text-sm font-medium border transition-colors ${form.pricing_mode === "hourly" ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>Paid by hour</button>
                </div>
              </div>
              {form.pricing_mode === "quote" ? (
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Quote amount (£)</label>
                  <input data-testid="wo-quote-total" type="number" min="0" step="0.01" value={form.quote_total} onChange={(e) => setForm({ ...form, quote_total: e.target.value })} placeholder="0.00" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Travel hours</label>
                      <input data-testid="wo-travel-hours" type="number" min="0" step="0.5" value={form.travel_hours} onChange={(e) => setForm({ ...form, travel_hours: e.target.value })} placeholder="0" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Labour hours</label>
                      <input data-testid="wo-labour-hours" type="number" min="0" step="0.5" value={form.labour_hours} onChange={(e) => setForm({ ...form, labour_hours: e.target.value })} placeholder="0" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
                  </div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Materials cost (£)</label>
                    <input data-testid="wo-cost" type="number" min="0" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="0.00" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
                </>
              )}
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea data-testid="wo-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Notes <span className="text-xs font-normal text-slate-400">(key location, access, etc.)</span></label>
                <textarea data-testid="wo-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Key safe 0161 · access after 5pm" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              {!editing && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Attachments <span className="text-xs font-normal text-slate-400">(quotes, photos, invoices — optional)</span></label>
                <label data-testid="wo-file-label" className="w-full border-2 border-dashed border-slate-200 rounded-lg py-3 flex items-center justify-center gap-2 text-sm text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors cursor-pointer">
                  <Paperclip size={15} /> {files.length ? `${files.length} file${files.length > 1 ? "s" : ""} selected` : "Attach files"}
                  <input data-testid="wo-file-input" type="file" multiple accept="image/*,video/*,application/pdf" className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                </label>
                {files.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {files.map((f, i) => (
                      <span key={i} className="text-xs bg-slate-100 border border-slate-200 rounded px-2 py-1 flex items-center gap-1 max-w-[160px]">
                        <span className="truncate">{f.name}</span>
                        <button type="button" onClick={() => setFiles(files.filter((_, x) => x !== i))} className="text-slate-400 hover:text-red-500"><X size={11} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              )}
              <button type="submit" data-testid="save-work-order" disabled={busy} className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <ClipboardList size={16} />} {editing ? "Save Changes" : "Create Work Order"}
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

function SummaryCard({ label, value, icon: Icon, tone, testid }) {
  const tones = { blue: "text-blue-700 bg-blue-50", red: "text-red-600 bg-red-50", emerald: "text-emerald-600 bg-emerald-50" };
  return (
    <Card className="p-5 flex items-center gap-4" data-testid={testid}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tones[tone]}`}><Icon size={20} /></div>
      <div>
        <p className="text-2xl font-bold text-slate-900 tabular-nums leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </Card>
  );
}

function TableTracker({ shown, properties, setLocal, commit, setStatus, totalCost, openEdit, remove }) {
  const addr = (o) => {
    const p = (properties || []).find((x) => x.reference === o.property_ref);
    return p?.address || o.property_ref || "—";
  };
  const num = (o, field) => (
    <input type="number" step="0.5" min="0" value={o[field] ?? ""}
      onChange={(e) => setLocal(o.work_order_id, { [field]: e.target.value })}
      onBlur={(e) => { const v = parseFloat(e.target.value) || 0; setLocal(o.work_order_id, { [field]: v }); commit(o, { [field]: v }); }}
      data-testid={`wo-${field}-${o.work_order_id}`}
      className="w-16 rounded border border-slate-200 px-1.5 py-1 text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-blue-500" />
  );
  const HEADERS = ["Job ID", "Job Description", "Address", "Tenant", "Phone", "Urgency", "Reported", "Scheduled", "Status", "Quote £", "Travel", "Labour", "Materials £", "Total £", "Notes", ""];
  return (
    <Card className="p-0 overflow-hidden" data-testid="work-orders-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="bg-slate-800 text-white text-[11px] uppercase tracking-wide">
              {HEADERS.map((h, i) => <th key={i} className="px-3 py-2.5 text-left font-semibold">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {shown.map((o) => (
              <tr key={o.work_order_id} className="hover:bg-slate-50/60" data-testid={`wo-row-${o.work_order_id}`}>
                <td className="px-3 py-2 font-mono text-xs text-slate-400">{o.reference}</td>
                <td className="px-3 py-2 font-medium text-slate-800 max-w-[220px] truncate" title={o.title}>{o.title}</td>
                <td className="px-3 py-2 text-slate-500 max-w-[200px] truncate" title={addr(o)}>{addr(o)}</td>
                <td className="px-3 py-2 text-slate-500">{o.tenant_name || "—"}</td>
                <td className="px-3 py-2 text-slate-500 tabular-nums">{o.tenant_phone || "—"}</td>
                <td className="px-3 py-2">
                  <select value={o.priority} onChange={(e) => { setLocal(o.work_order_id, { priority: e.target.value }); commit(o, { priority: e.target.value }); }}
                    data-testid={`wo-urgency-${o.work_order_id}`}
                    className={`text-xs font-semibold rounded-full px-2.5 py-1 ring-1 ring-inset focus:outline-none ${URGENCY_STYLE[o.priority] || URGENCY_STYLE.Medium}`}>
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2 text-slate-500">{fmtDate(o.date_reported)}</td>
                <td className="px-3 py-2 text-slate-500">{fmtDate(o.target_date)}</td>
                <td className="px-3 py-2">
                  <select value={o.status} onChange={(e) => setStatus(o, e.target.value)} data-testid={`wo-status-select-${o.work_order_id}`}
                    className={`text-xs font-medium rounded-full px-2.5 py-1 ring-1 ring-inset focus:outline-none ${STATUS_STYLE[o.status]}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">{num(o, "quote_total")}</td>
                <td className="px-3 py-2">{num(o, "travel_hours")}</td>
                <td className="px-3 py-2">{num(o, "labour_hours")}</td>
                <td className="px-3 py-2">{num(o, "cost")}</td>
                <td className="px-3 py-2 font-semibold tabular-nums text-slate-800" data-testid={`wo-total-${o.work_order_id}`}>{gbp(totalCost(o))}</td>
                <td className="px-3 py-2">
                  <input value={o.notes ?? ""} onChange={(e) => setLocal(o.work_order_id, { notes: e.target.value })}
                    onBlur={(e) => commit(o, { notes: e.target.value })} placeholder="—" data-testid={`wo-notes-${o.work_order_id}`}
                    className="w-40 rounded border border-slate-200 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(o)} data-testid={`wo-edit-${o.work_order_id}`} className="text-slate-400 hover:text-blue-600 p-1" title="Edit"><Pencil size={14} /></button>
                    <button onClick={() => remove(o)} data-testid={`wo-delete-row-${o.work_order_id}`} className="text-slate-300 hover:text-red-500 p-1" title="Delete"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function WorkOrderComms({ workOrderId, contractorName }) {  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadComments = () => api.get(`/comments?entity_type=work_order&entity_id=${workOrderId}`).then(({ data }) => setComments(data)).catch(() => {});
  const loadAttachments = () => api.get(`/files?entity_type=work_order&entity_id=${workOrderId}&bucket=attachments`).then(({ data }) => setAttachments(data)).catch(() => {});

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) { loadComments(); loadAttachments(); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/comments`, { entity_type: "work_order", entity_id: workOrderId, body: text.trim() });
      setText("");
      await loadComments();
    } catch { toast.error("Could not send message"); }
    finally { setSending(false); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("entity_type", "work_order");
        fd.append("entity_id", workOrderId);
        fd.append("bucket", "attachments");
        fd.append("file", f);
        await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded`);
      await loadAttachments();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`wo-comms-${workOrderId}`}>
      <button data-testid={`wo-toggle-comms-${workOrderId}`} onClick={toggle}
        className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-blue-700 transition-colors">
        <MessageSquare size={15} /> {contractorName ? `Chat with ${contractorName}` : "Contractor chat"}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
            {comments.length === 0 && attachments.length === 0 && (
              <p className="text-xs text-slate-400">No messages yet. Send an update, ask a question, or share photos/documents with the contractor.</p>
            )}
            {comments.map((c) => (
              <div key={c.comment_id} data-testid={`wo-comment-${c.comment_id}`}
                className={`rounded-lg px-3 py-2 text-sm ${c.author_role === "contractor" ? "bg-amber-50" : "bg-slate-50"}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-medium text-slate-800 text-xs">{c.author_name}</span>
                  <Badge>{c.author_role}</Badge>
                  <span className="text-[11px] text-slate-400 ml-auto">{fmtDate(c.date)}</span>
                </div>
                <p className="text-slate-700 whitespace-pre-wrap">{c.body}</p>
              </div>
            ))}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {attachments.map((f) => (
                  <a key={f.file_id} href={fileUrl(f.file_id)} target="_blank" rel="noreferrer" data-testid={`wo-att-${f.file_id}`}
                    className="w-16 h-16 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center hover:ring-2 hover:ring-blue-500 transition-all">
                    {f.category === "photo" ? <img src={fileUrl(f.file_id)} alt="" className="w-full h-full object-cover" />
                      : f.category === "video" ? <video src={fileUrl(f.file_id)} className="w-full h-full object-cover" />
                      : <FileText size={18} className="text-slate-400" />}
                  </a>
                ))}
              </div>
            )}
          </div>
          <form onSubmit={send} className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*,video/*,application/pdf" multiple className="hidden" data-testid={`wo-comms-file-${workOrderId}`} onChange={onUpload} />
            <button type="button" data-testid={`wo-comms-attach-${workOrderId}`} onClick={() => fileRef.current?.click()} disabled={uploading}
              className="w-9 h-9 shrink-0 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 flex items-center justify-center disabled:opacity-60" title="Attach photos, videos or documents">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}
            </button>
            <input data-testid={`wo-comms-input-${workOrderId}`} value={text} onChange={(e) => setText(e.target.value)} placeholder="Message the contractor…"
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <button type="submit" data-testid={`wo-comms-send-${workOrderId}`} disabled={sending || !text.trim()}
              className="w-9 h-9 shrink-0 rounded-lg bg-blue-700 text-white hover:bg-blue-800 flex items-center justify-center disabled:opacity-50">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
